import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Input, SpaceBetween, Header, Select, Table, Tabs, Spinner, Icon } from '@cloudscape-design/components';
import di from '../../../di/DependencyInjection';
import { GetS3RagPathUseCase, GetS3RagPathUseCaseName } from '../../../domain/use_cases/ai/GetS3RagPathUseCase';
import { GetS3RagRegisteredPathUseCase, GetS3RagRegisteredPathUseCaseName } from '../../../domain/use_cases/ai/GetS3RagRegisteredPathUseCase';
import { GetS3RagListByCustomerUseCase, GetS3RagListByCustomerUseCaseName } from '../../../domain/use_cases/ai/GetS3RagListByCustomerUseCase';
import { GetAICustomersUseCase, GetAICustomersUseCaseName } from '../../../domain/use_cases/ai/GetAICustomersUseCase';
import { isRight } from 'fp-ts/lib/Either';

import "./AiRagUpload.scss";
import { DeleteS3RagDocumentUseCase, DeleteS3RagDocumentUseCaseName } from '../../../domain/use_cases/ai/DeleteS3RagDocumentUseCase';
import Icons from '../../assets/Icons';

const AiRagUpload: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [customerUpload, setCustomerUpload] = useState<any>([]);
    const [customerListing, setCustomerListing] = useState<any>([]);
    const [customerOptions, setCustomerOptions] = useState<any>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [registeredSuccess, setRegisteredSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [s3Key, setS3Key] = useState('');
    const [uploadUrl, setUploadUrl] = useState('');
    const [ customerS3RagFiles, setCustomerS3RagFiles ] = useState<any>([]);
    const [activeTab, setActiveTab] = useState('upload');
    const [loadingListing, setLoadingListing] = useState(false);
    const [errorListing, setErrorListing] = useState<string | null>(null);
    const [deletingFile, setDeletingFile] = useState<string | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        console.log('onDrop called with:', acceptedFiles);
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
            setUploadSuccess(false);
            setRegisteredSuccess(false);
            setError(null);
        }
    }, []);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        onDrop,
        multiple: false,
        noClick: false,
    });

    const firstCall = async () => {
        const _getCustomers = async () => await di.get<GetAICustomersUseCase>(GetAICustomersUseCaseName)._aiRepository.getAICustomers('calls');
        const response = await _getCustomers();
        if (isRight(response)) {
            try {
                let result = JSON.parse(response.right as string);
                const keysArray = Object.keys(result.data);
                
                setCustomerOptions(keysArray);
            } catch (error) {
                setCustomerOptions([]);
            }
        }
    }

    const getListFromCustomer = async (customer: string) => {
        setLoadingListing(true);
        setCustomerS3RagFiles([]);
        setErrorListing(null);
        
        try {
            const getS3ListByCustomer = async () => await di.get<GetS3RagListByCustomerUseCase>(GetS3RagListByCustomerUseCaseName).call(customer);
            const response = await getS3ListByCustomer();
            if (isRight(response)) {
                setCustomerS3RagFiles(JSON.parse(response.right as string).files);
            }
        } catch (error) {
            console.error('Error fetching S3 files:', error);
            setErrorListing('Failed to fetch files');
        } finally {
            setLoadingListing(false);
        }
    }

    const handleDeleteFile = async (s3Key: string, customer: string) => {
        console.log('Delete file - S3 Key:', s3Key, 'Customer:', customer);
        setErrorListing(null);
        setDeletingFile(s3Key);
        
        try {
            const response = await di.get<DeleteS3RagDocumentUseCase>(DeleteS3RagDocumentUseCaseName).call(customer, s3Key);
            console.log(response);
            if (isRight(response)) {
                const parsed = JSON.parse(response.right as string);
                if (parsed.error) {
                    setErrorListing(parsed.error);
                    console.log(parsed.error);
                    
                } else {
                    console.log('File deleted successfully');
                    getListFromCustomer(customer);
                }
            } else {
                setErrorListing('Failed to delete file');
                console.error('Failed to delete file');
            }
        } catch (error) {
            setErrorListing('Error deleting file');
            console.error('Error deleting file:', error);
        } finally {
            setDeletingFile(null);
        }
    }

    useEffect(() => {
        firstCall();
    }, [])
    
    const handleUpload = async () => {
        if (!selectedFile || !customerUpload) {
            setError("Missing file or customer");
            return;
        }

        setUploading(true);
        setError(null);
        setUploadSuccess(false);
        setRegisteredSuccess(false);

        try {
            const filename = selectedFile.name;
            const response = await di.get<GetS3RagPathUseCase>(GetS3RagPathUseCaseName).call(filename, customerUpload);

            if (response._tag === 'Right') {
                const parsed = JSON.parse(response.right);
                const { upload_url, s3_key } = parsed;
                setUploadUrl(upload_url);
                setS3Key(s3_key);

                const uploadResp = await fetch(upload_url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/octet-stream',
                    },
                    body: selectedFile,
                });

                if (uploadResp.ok) {
                    setUploadSuccess(true);
                    console.log('File uploaded successfully to S3');

                    try {
                        const responseRegistered = await di
                            .get<GetS3RagRegisteredPathUseCase>(GetS3RagRegisteredPathUseCaseName)
                            .call(s3_key, customerUpload);

                        console.log('Registered response:', responseRegistered);

                        if (responseRegistered._tag === 'Right') {
                            setRegisteredSuccess(true);
                        } else {
                            setError('Upload succeeded, but failed to register the file');
                        }
                    } catch (registrationError: any) {
                        console.error('Error while registering upload:', registrationError);
                        setError('Upload succeeded, but registration failed');
                    }
                } else {
                    setError(`Upload failed: ${uploadResp.statusText}`);
                }
            } else {
                setError("Failed to get upload URL from backend");
            }
        } catch (err: any) {
            setError(`Error: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    const UploadSection = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0px',
            marginTop: "30px"
        }}>
            <div style={{ maxWidth: '100%', width: '100%' }}>
                <div style={{ height: '80px' }}>
                    <Header variant="h1" description="Upload documentation to RAG storage by customer">
                        RAG Document Upload
                    </Header>
                </div>

                <SpaceBetween size="m" direction="vertical">
                    <Select
                        placeholder="Select customer"
                        selectedOption={
                            customerUpload ? { label: customerUpload, value: customerUpload } : null
                        }
                        onChange={({ detail }) => {
                            setCustomerUpload(detail.selectedOption.value);
                        }}
                        options={customerOptions.map((c: any) => ({
                            label: c,
                            value: c
                        }))}
                    />

                    <div
                        {...getRootProps()}
                        style={{
                            border: '2px dashed #888',
                            borderRadius: '8px',
                            padding: '30px',
                            textAlign: 'center',
                            backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
                            cursor: 'pointer',
                        }}
                    >
                        <input {...getInputProps()} />
                        <p>{isDragActive ? 'Drop the file here...' : 'Drag & drop a file here, or click to select one'}</p>
                        {selectedFile && <p><strong>Selected:</strong> {selectedFile.name}</p>}
                    </div>

                    <Button onClick={handleUpload} disabled={!selectedFile || !customerUpload || uploading} loading={uploading}>
                        Upload File to S3
                    </Button>

                    {uploadSuccess && (
                        <div style={{ color: 'green' }}>
                            File uploaded to S3: <code>{s3Key}</code>
                        </div>
                    )}

                    {registeredSuccess && (
                        <div style={{ color: 'blue' }}>
                            File successfully registered in the system.
                        </div>
                    )}

                    {error && (
                        <div style={{ color: 'red' }}>
                            {error}
                        </div>
                    )}
                </SpaceBetween>
            </div>
        </div>
    );

    const ListingSection = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '0px',
            marginTop: "30px"
        }}>
            <div style={{ width: '100%' }}>
                <div style={{ height: '80px' }}>
                    <Header variant="h1" description="View existing RAG documents by customer">
                        RAG Document Listing
                    </Header>
                </div>

                <SpaceBetween size="m" direction="vertical">
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <Select
                                placeholder="Select customer"
                                selectedOption={
                                    customerListing ? { label: customerListing, value: customerListing } : null
                                }
                                onChange={({ detail }) => {
                                    setCustomerListing(detail.selectedOption.value);
                                    getListFromCustomer(detail.selectedOption.value || "");
                                }}
                                options={customerOptions.map((c: any) => ({
                                    label: c,
                                    value: c
                                }))}
                            />
                        </div>
                        {customerListing && (
                            <Button
                                variant="icon"
                                iconName="refresh"
                                onClick={() => getListFromCustomer(customerListing)}
                                disabled={loadingListing}
                                loading={loadingListing}
                            />
                        )}
                    </div>

                    {loadingListing ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <Spinner size="large" />
                            <div style={{ marginTop: '10px' }}>Loading files...</div>
                        </div>
                    ) : customerS3RagFiles.length > 0 ? (
                        <div className="scroll_container_rag" >
                            <table
                            style={{
                                width: '100%',
                                marginLeft: '0px',
                                tableLayout: 'auto',
                                boxSizing: 'border-box',
                            }}
                            >
                            <thead
                                style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: '#fff',
                                zIndex: 1,
                                boxSizing: 'border-box',
                                }}
                            >
                                <tr>
                                <th style={{ border: '1px solid #ccc', padding: 8, boxSizing: 'border-box', width: '25%' }}>
                                    File Name
                                </th>
                                <th style={{ border: '1px solid #ccc', padding: 8, boxSizing: 'border-box', width: '70%' }}>
                                    S3 Key
                                </th>
                                <th style={{ border: '1px solid #ccc', padding: 8, boxSizing: 'border-box', width: '5%', minWidth: '60px', textAlign: 'center' }}>
                                    Actions
                                </th>
                                </tr>
                            </thead>
                            <tbody>
                                {customerS3RagFiles.map((item: any, index: any) => {
                                const parts = item.split('/');
                                const fileName = parts[parts.length - 1];
                                return (
                                    <tr key={index}>
                                    <td style={{ border: '1px solid #ccc', padding: 8, boxSizing: 'border-box', width: '25%' }}>
                                        {fileName}
                                    </td>
                                    <td style={{ border: '1px solid #ccc', padding: 8, boxSizing: 'border-box', width: '70%' }}>
                                        {item}
                                    </td>
                                    <td style={{ border: '1px solid #ccc', padding: 12, boxSizing: 'border-box', width: '5%', minWidth: '60px', textAlign: 'center' }}>
                                        <Button 
                                            variant="link" 
                                            onClick={() => handleDeleteFile(item, customerListing)}
                                            disabled={deletingFile === item}
                                        >
                                            {deletingFile === item ? (
                                                <Spinner size="normal" />
                                            ) : (
                                                <span style={{ color: '#d13212' }}>
                                                    <Icons.Trash className='trash-icon' />
                                                </span>
                                            )}
                                        </Button>
                                    </td>
                                    </tr>
                                );
                                })}
                            </tbody>
                            </table>
                        </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                {customerListing ? 'No files found for this customer' : 'Please select a customer to view files'}
                            </div>
                        )}
                        {errorListing && (
                            <div style={{ color: 'red', textAlign: 'center', padding: '10px' }}>
                                {errorListing}
                            </div>
                        )}
                </SpaceBetween>
            </div>
        </div>
    );

    return (
        <>
            <div style={{ marginBottom: '0px', marginTop: '-40px' }}>
                <Tabs
                    activeTabId={activeTab}
                    onChange={({ detail }) => setActiveTab(detail.activeTabId)}
                    tabs={[
                        {
                            label: 'Upload Documents',
                            id: 'upload',
                            content: <DropZone />
                        },
                        {
                            label: 'View Documents',
                            id: 'listing',
                            content: <ListingSection />
                        }
                    ]}
                />
            </div>
        </>
    );
};



const DropZone: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [customer, setCustomer] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [registeredSuccess, setRegisteredSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [s3Key, setS3Key] = useState('');
    const [uploadUrl, setUploadUrl] = useState('');
    const [customerOptions, setCustomerOptions] = useState<any>([]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setSelectedFile(acceptedFiles[0]);
        setUploadSuccess(false);
        setRegisteredSuccess(false);
        setError(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const firstCall = async () => {
        const _getCustomers = async () => await di.get<GetAICustomersUseCase>(GetAICustomersUseCaseName)._aiRepository.getAICustomers('calls');
        const response = await _getCustomers();
        if (isRight(response)) {
            try {
                let result = JSON.parse(response.right as string);
                const keysArray = Object.keys(result.data);
                
                setCustomerOptions(keysArray);
            } catch (error) {
                setCustomerOptions([]);
            }
        }
    }

    useEffect(() => {
        firstCall();
    }, [])

    const handleUpload = async () => {
        if (!selectedFile || !customer) {
            setError("Missing file or customer");
            return;
        }

        setUploading(true);
        setError(null);
        setUploadSuccess(false);
        setRegisteredSuccess(false);

        try {
            const filename = selectedFile.name;
            const response = await di.get<GetS3RagPathUseCase>(GetS3RagPathUseCaseName).call(filename, customer);

            if (response._tag === 'Right') {
                const parsed = JSON.parse(response.right);
                const { upload_url, s3_key } = parsed;
                setUploadUrl(upload_url);
                setS3Key(s3_key);

                const uploadResp = await fetch(upload_url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/octet-stream',
                    },
                    body: selectedFile,
                });

                if (uploadResp.ok) {
                    setUploadSuccess(true);
                    console.log('File uploaded successfully to S3');

                    try {
                        const responseRegistered = await di
                            .get<GetS3RagRegisteredPathUseCase>(GetS3RagRegisteredPathUseCaseName)
                            .call(s3_key, customer);

                        console.log('Registered response:', responseRegistered);

                        if (responseRegistered._tag === 'Right') {
                            setRegisteredSuccess(true);
                        } else {
                            setError('Upload succeeded, but failed to register the file');
                        }
                    } catch (registrationError: any) {
                        console.error('Error while registering upload:', registrationError);
                        setError('Upload succeeded, but registration failed');
                    }
                } else {
                    setError(`Upload failed: ${uploadResp.statusText}`);
                }
            } else {
                setError("Failed to get upload URL from backend");
            }
        } catch (err: any) {
            setError(`Error: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '0px',
            marginTop: "30px"
        }}>
            <div style={{ width: '100%' }}>
                <div style={{ height: '80px' }}>
                    <Header variant="h1" description="Upload documentation to RAG storage by customer">
                        RAG Document Upload
                    </Header>
                </div>

                <SpaceBetween size="m" direction="vertical">
                    <Select
                        placeholder="Select customer"
                        selectedOption={
                            customer ? { label: customer, value: customer } : null
                        }
                        onChange={({ detail }) => {
                            setCustomer(detail.selectedOption.value || '');
                        }}
                        options={customerOptions.map((c: any) => ({
                            label: c,
                            value: c
                        }))}
                    />

                    <div
                        {...getRootProps()}
                        style={{
                            border: '2px dashed #888',
                            borderRadius: '8px',
                            padding: '100px',
                            textAlign: 'center',
                            backgroundColor: isDragActive ? '#eee' : '#fafafa',
                            cursor: 'pointer'
                        }}
                    >
                        <input {...getInputProps()} />
                        {
                            isDragActive
                                ? <p>Drop the file here ...</p>
                                : <p>Drag & drop a file here, or click to select</p>
                        }
                        {selectedFile && (
                            <p><strong>Selected file:</strong> {selectedFile.name}</p>
                        )}
                    </div>

                    <Button onClick={handleUpload} disabled={!selectedFile || !customer || uploading} loading={uploading}>
                        Upload File to S3
                    </Button>

                    {uploadSuccess && (
                        <div style={{ color: 'green' }}>
                            File uploaded to S3: <code>{s3Key}</code>
                        </div>
                    )}

                    {registeredSuccess && (
                        <div style={{ color: 'blue' }}>
                            File successfully registered in the system.
                        </div>
                    )}

                    {error && (
                        <div style={{ color: 'red' }}>
                            {error}
                        </div>
                    )}
                </SpaceBetween>
            </div>
        </div>
    );
};


export default AiRagUpload;
